import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback } from 'react';

import { useGetUserUtils } from '~/data/user';
import {
  fetchUserGroup,
  fetchUserGroups,
  queryKeysUserGroup,
} from '~/data/userGroup';

import { type Option, SelectServerDriven } from './SelectServerDriven';
import { createGetItemData, createLoadOptions } from './utils';

const getOptionLabel = ({ name }) => name;

// Define a UserGroup type based on the expected structure; TODO: this should come as response type from the request.
type UserGroup = {
  id: string;
  name: string;
};

type P = {
  readonly className?: string;
  readonly formatOptionLabel?: (option: Option<UserGroup>) => React.ReactNode;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<UserGroup>) => boolean;
  readonly onChange: (value: Array<{ id: string }>) => void;
  readonly placeholder?: string;
  readonly style?: React.CSSProperties;
  readonly value: string[];
};

export const MultiSelectUserGroups = ({
  className,
  formatOptionLabel,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  onChange,
  placeholder = 'Benutzer-Gruppen',
  style,
  value,
}: P) => {
  const queryClient = useQueryClient();

  const { UserUtils } = useGetUserUtils();

  /**
   * Loads the options for the multi-select dropdown.
   * @param {string} searchString - The search string entered by the user.
   * @param {unknown} loadedOptions - The already loaded options.
   * @param {{ page: number }} - The current page number.
   */
  const loadOptions = createLoadOptions<UserGroup>({
    createQueryKey: ({ page, ...queryParams }) =>
      queryKeysUserGroup.getAll(queryParams),
    async fetchItems({ page, ...queryParams }) {
      const { data, hasNextPage } = await fetchUserGroups(queryParams);

      return {
        data,
        hasNextPage,
      };
    },
    getOptionLabel,
    queryClient,
  });

  /**
   * Gets user group data for a user group with a given userGroupId.
   * Attempts to get it from the query cache and fetches it from the API
   * if the user group data is not in the cache.
   * @param {string} userGroupId - The ID of the user group to get.
   */
  const getItemData = createGetItemData<UserGroup>({
    createQueryKey: (userGroupId) => queryKeysUserGroup.get(userGroupId),
    fetchItem: fetchUserGroup,
    queryClient,
    queryKeyBase: [queryKeysUserGroup.base()],
  });

  // This is just a glue function b/c PermissionGrantMultiPicker currently expects to pick the ID from an object in its onChange function. Should be refactored.
  const handleChange = useCallback(
    (newValues: string[]) => {
      onChange(newValues.map((id) => ({ id })));
    },
    [onChange],
  );

  return (
    <SelectServerDriven<UserGroup>
      className={className}
      formatOptionLabel={formatOptionLabel}
      getItemData={getItemData}
      getOptionLabel={getOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isItemSelectionAllowed={UserUtils.isUserGroupReadAllowedUser}
      isMultiSelect={isMultiSelect}
      isRemovableValue={isRemovableValue}
      loadOptions={loadOptions}
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={handleChange}
    />
  );
};
